"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { AeraButton, ModalCloseButton, ModalShell } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import { postCreateReceivableAndAllocate, pickOperationId } from "@/lib/moneyOperations";
import { pickPaymentRevision } from "@/lib/receivableCorrections";
import { offerMoneyUndoToast, pickMoneyUndoSourceLabel, formatAllocateUndoLabel } from "@/lib/moneyUndoToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";

const RECORD_DRAWER_PANEL_SELECTOR =
  "[data-aera-modal-panel].record-drawer-modal-panel";

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function pickField(item: ReceivableListItem, keys: string[]): string {
  for (const key of keys) {
    const value = item.fields.find((f) => f.key === key)?.value?.trim();
    if (value && value !== "—") return value;
  }
  return "";
}

/** Strip ฿ / commas so API Decimal("2100.00") works. */
function normalizeAmountInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "")
  );
}

function parseAmountNumber(raw: string): number {
  const value = Number.parseFloat(normalizeAmountInput(raw));
  return Number.isFinite(value) ? value : 0;
}

function formatAmount(value: number): string {
  return value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function amountInputValue(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";
  return String(Number(value.toFixed(2)));
}

function pickPaymentId(item: ReceivableListItem): string {
  return (
    item.paymentId?.trim() ||
    pickField(item, ["payment_id", "paymentId"]) ||
    ""
  );
}

function pickRemaining(item: ReceivableListItem): string {
  const raw =
    pickField(item, [
      "amount_remaining",
      "slip_amount",
      "unallocated_amount",
      "amount",
    ]) ||
    item.amountLabel ||
    "";
  return normalizeAmountInput(raw);
}

function MaxAmountButton({
  disabled,
  disabledReason,
  label,
  onClick,
}: {
  disabled: boolean;
  disabledReason?: string;
  label: string;
  onClick: () => void;
}) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide opacity-100 transition-colors ${
        disabled
          ? "cursor-not-allowed text-aera-gray pointer-events-none"
          : "cursor-pointer text-aera-amberdark hover:bg-aera-amber/15"
      }`}
      aria-label={disabledReason ? `MAX — ${disabledReason}` : label}
    >
      MAX
    </button>
  );

  if (!disabledReason) {
    return <span className="inline-flex shrink-0">{button}</span>;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0 cursor-not-allowed">{button}</span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        collisionPadding={12}
        className="z-[10100] w-max max-w-[14rem] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
      >
        <p>{disabledReason}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function CreateReceivableFromSlipModal({
  open,
  onOpenChange,
  slipItem,
  ownerUserId,
  workspaceId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slipItem: ReceivableListItem;
  ownerUserId: string | null;
  workspaceId: string | null;
  onCreated?: () => void | Promise<void>;
}) {
  const paymentId = useMemo(() => pickPaymentId(slipItem), [slipItem]);
  const remaining = useMemo(() => pickRemaining(slipItem), [slipItem]);
  const remainingAmount = useMemo(() => parseAmountNumber(remaining), [remaining]);
  const defaultName =
    pickField(slipItem, ["sender_name", "customer_ref", "customer_name"]) ||
    slipItem.title ||
    "";

  const [customerName, setCustomerName] = useState(defaultName);
  const [amount, setAmount] = useState(remaining);
  const [allocateAmount, setAllocateAmount] = useState(remaining);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!open) return;
    setScopedHost(document.querySelector(RECORD_DRAWER_PANEL_SELECTOR));
  }, [isDesktop, open]);

  const isScoped = isDesktop && scopedHost != null;

  useEffect(() => {
    if (!open) return;
    setCustomerName(defaultName);
    setAmount(remaining);
    setAllocateAmount(remaining);
    setError(null);
    setBusy(false);
  }, [open, defaultName, remaining, slipItem.mergeKey]);

  const canSubmit = Boolean(
    open &&
      ownerUserId?.trim() &&
      workspaceId?.trim() &&
      paymentId &&
      customerName.trim() &&
      amount.trim(),
  );

  const fullAmountValue = parseAmountNumber(amount);
  const allocateValue = parseAmountNumber(allocateAmount);
  const maxFullAmount = Math.max(0, remainingAmount);
  const maxAllocateAmount = Math.max(
    0,
    Math.min(
      remainingAmount,
      fullAmountValue > 0 ? fullAmountValue : remainingAmount,
    ),
  );

  const fullAlreadyMax = Math.abs(fullAmountValue - maxFullAmount) < 0.005;
  const allocateAlreadyMax =
    Math.abs(allocateValue - maxAllocateAmount) < 0.005;
  const fullMaxDisabled = busy || maxFullAmount <= 0.009 || fullAlreadyMax;
  const allocateMaxDisabled =
    busy || maxAllocateAmount <= 0.009 || allocateAlreadyMax;

  const fullMaxDisabledReason = busy
    ? "กำลังทำงานอยู่ กรุณารอสักครู่"
    : fullAlreadyMax
      ? `ยอดเต็มตามยอดสลิปแล้ว (฿${formatAmount(maxFullAmount)})`
      : maxFullAmount <= 0.009
        ? "ไม่มียอดสลิปเหลือให้ใส่"
        : undefined;

  const allocateMaxDisabledReason = busy
    ? "กำลังทำงานอยู่ กรุณารอสักครู่"
    : allocateAlreadyMax
      ? Math.abs(maxAllocateAmount - remainingAmount) < 0.005
        ? `ยอดเต็มตามยอดสลิปแล้ว (฿${formatAmount(maxAllocateAmount)})`
        : `ยอดเต็มตามยอดเต็มแล้ว (฿${formatAmount(maxAllocateAmount)})`
      : maxAllocateAmount <= 0.009
        ? "ไม่มียอดเหลือให้จัดสรร"
        : undefined;

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !ownerUserId || !workspaceId) return;
    const normalizedAmount = normalizeAmountInput(amount);
    const normalizedAllocate = normalizeAmountInput(
      allocateAmount.trim() || remaining,
    );
    if (!normalizedAmount || Number.parseFloat(normalizedAmount) <= 0) {
      setError("กรุณากรอกยอดเต็มให้ถูกต้อง");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await postCreateReceivableAndAllocate({
        ownerUserId,
        workspaceId,
        paymentId,
        customerName: customerName.trim(),
        amount: normalizedAmount,
        allocateAmount: normalizedAllocate || undefined,
        expectedPaymentRevision: pickPaymentRevision(slipItem),
      });
      const allocateNum = Number.parseFloat(normalizedAllocate || "0");
      const receivableName = customerName.trim() || "ลูกค้า";
      const slipName = pickMoneyUndoSourceLabel(slipItem) || "";
      offerMoneyUndoToast({
        message: formatAllocateUndoLabel(
          [
            {
              label: receivableName,
              amount: Number.isFinite(allocateNum) ? allocateNum : 0,
            },
          ],
          "จับคู่ยอดค้าง",
          "สร้างยอดค้างและจับคู่สำเร็จ",
          { fromLabel: slipName || undefined },
        ),
        ownerUserId,
        workspaceId,
        operationId: pickOperationId(result),
        onUndone: onCreated,
      });
      await onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "สร้างยอดค้างไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={close}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={busy}
      zIndexClass="z-[10050]"
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            <span className="flex items-center gap-2 text-aera-green">
              <Plus className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
              สร้างยอดค้างใหม่
            </span>
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-aera-gray">
            สลิปเหลือรอจัดสรรประมาณ{" "}
            <span className="font-semibold tabular-nums text-aera-charcoal">
              {remaining
                ? `฿${Number(remaining).toLocaleString("th-TH")}`
                : "—"}
            </span>
          </p>
        </div>
        <ModalCloseButton
          onClose={close}
          disabled={busy}
          className="-mr-1 shrink-0"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
        <TooltipProvider delayDuration={200}>
          <div className="space-y-3">
            {!paymentId ? (
              <p className="rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
                ไม่พบ payment_id ของสลิป — ยังสร้างยอดจากสลิปนี้ไม่ได้
              </p>
            ) : (
              <>
                <label className="block space-y-1 text-xs font-semibold text-aera-gray">
                  ชื่อลูกค้า / รายการ
                  <input
                    className="w-full rounded-lg border border-aera-lightgray bg-white px-3 py-2 text-sm font-semibold text-aera-charcoal outline-none focus:border-aera-amber"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={busy}
                  />
                </label>
                <label className="block space-y-1 text-xs font-semibold text-aera-gray">
                  ยอดเต็ม
                  <div className="flex items-center gap-1.5 rounded-lg border border-aera-lightgray bg-white px-3 py-2 focus-within:border-aera-amber">
                    <span className="text-sm font-semibold text-aera-gray">฿</span>
                    <input
                      className="min-w-0 flex-1 bg-transparent text-sm font-semibold tabular-nums text-aera-charcoal outline-none disabled:opacity-60"
                      value={amount}
                      onChange={(e) =>
                        setAmount(normalizeAmountInput(e.target.value))
                      }
                      disabled={busy}
                      inputMode="decimal"
                    />
                    <MaxAmountButton
                      disabled={fullMaxDisabled}
                      disabledReason={fullMaxDisabledReason}
                      label="ใส่ยอดเต็มสูงสุดจากสลิป"
                      onClick={() => setAmount(amountInputValue(maxFullAmount))}
                    />
                  </div>
                </label>
                <label className="block space-y-1 text-xs font-semibold text-aera-gray">
                  จัดสรรจากสลิปนี้
                  <div className="flex items-center gap-1.5 rounded-lg border border-aera-lightgray bg-white px-3 py-2 focus-within:border-aera-amber">
                    <span className="text-sm font-semibold text-aera-gray">฿</span>
                    <input
                      className="min-w-0 flex-1 bg-transparent text-sm font-semibold tabular-nums text-aera-charcoal outline-none disabled:opacity-60"
                      value={allocateAmount}
                      onChange={(e) =>
                        setAllocateAmount(normalizeAmountInput(e.target.value))
                      }
                      disabled={busy}
                      inputMode="decimal"
                    />
                    <MaxAmountButton
                      disabled={allocateMaxDisabled}
                      disabledReason={allocateMaxDisabledReason}
                      label="ใส่ยอดจัดสรรสูงสุดจากสลิป"
                      onClick={() =>
                        setAllocateAmount(amountInputValue(maxAllocateAmount))
                      }
                    />
                  </div>
                </label>
              </>
            )}
            {error ? (
              <p className="rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
                {error}
              </p>
            ) : null}
          </div>
        </TooltipProvider>
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant="primary"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          loading={busy}
          disabled={!canSubmit || busy}
          onClick={() => void handleSubmit()}
        >
          {busy ? "กำลังสร้าง…" : "สร้างและจับคู่"}
        </AeraButton>
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          disabled={busy}
          onClick={close}
        >
          ยกเลิก
        </AeraButton>
      </div>
    </ModalShell>
  );
}
